import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { getQrCode } from "../modules/api/RecordInvoiceAPI";
import ExternalBrowserNotice from "../modules/components/ExternalBrowserNotice";
import { getSaveMethod, reopenInExternalBrowser } from "../modules/utils/documentDownload";
import {
  QR_CODE_FILE_NAME,
  QR_CODE_MIME_TYPE,
  QR_CODE_SAVE_MESSAGES,
  normalizeQrCode,
  saveQrCode,
  usePreparedQrCode,
} from "../modules/utils/qrCodeDownload";

const QrCodeViewerPage = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const saveMethod = useMemo(() => getSaveMethod(QR_CODE_FILE_NAME, QR_CODE_MIME_TYPE), []);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveStarted = useRef(false);
  const preparedQrCode = usePreparedQrCode(qrCodeUrl);

  const paymentAmount = params.get("_amt") || "";

  useEffect(() => {
    const customerWaterId = Number(params.get("_id"));

    if (!localStorage.getItem("token") || !customerWaterId) {
      setError("ไม่พบข้อมูล QR Code");
      return;
    }

    getQrCode(customerWaterId)
      .then((response: any) => {
        if (response?.responseStatus === "true" || response?.responseStatus === true) {
          const nextQrCodeUrl = normalizeQrCode(response?.data?.qrcode);

          if (!nextQrCodeUrl) {
            setError("ไม่พบข้อมูล QR Code");
            return;
          }

          setQrCodeUrl(nextQrCodeUrl);
          return;
        }

        setError(response?.responseMessage || "ไม่สามารถรับ QR Code ได้ กรุณาลองใหม่อีกครั้ง");
      })
      .catch(() => {
        setError("ไม่สามารถรับ QR Code ได้ กรุณาลองใหม่อีกครั้ง");
      });
  }, [params]);

  const handleSave = useCallback(async () => {
    if (!qrCodeUrl) return;

    setIsSaving(true);
    setNotice("");
    try {
      const outcome = await saveQrCode(qrCodeUrl, preparedQrCode, reopenInExternalBrowser);
      setNotice(QR_CODE_SAVE_MESSAGES[outcome]);
    } catch (saveError) {
      console.error("Error downloading QR code:", saveError);
      setNotice("บันทึกรูป QR Code ไม่สำเร็จ กรุณากดค้างที่รูปแล้วเลือกบันทึกรูปภาพ");
    } finally {
      setIsSaving(false);
    }
  }, [qrCodeUrl, preparedQrCode]);

  // Android browsers download without a tap, finishing what the user started in
  // LINE. The iOS share sheet needs a real tap, so iOS waits for the button.
  useEffect(() => {
    if (saveMethod !== "download" || !preparedQrCode || autoSaveStarted.current) return;
    autoSaveStarted.current = true;
    handleSave();
  }, [saveMethod, preparedQrCode, handleSave]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        padding: "24px",
        background: "#F5F7F8",
        fontFamily: "Google Sans, sans-serif",
        textAlign: "center",
      }}
    >
      {!qrCodeUrl && !error ? (
        <>
          <ProgressSpinner style={{ width: "56px", height: "56px" }} strokeWidth="4" />
          <div style={{ color: "#2C2C2C", fontSize: "16px" }}>กำลังเตรียม QR Code...</div>
        </>
      ) : null}

      {error ? <div style={{ color: "#2C2C2C", fontSize: "16px" }}>{error}</div> : null}

      {qrCodeUrl ? (
        <>
          <div style={{ color: "#2C2C2C", fontSize: "18px", fontWeight: 700 }}>
            QR Code สำหรับชำระเงิน
          </div>

          <div
            style={{
              width: "100%",
              maxWidth: "320px",
              background: "#FFFFFF",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <img
              src={preparedQrCode?.previewUrl || qrCodeUrl}
              alt="QR Code"
              onError={() => setError("ไม่สามารถแสดง QR Code ได้ กรุณาลองใหม่อีกครั้ง")}
              style={{ width: "100%", height: "auto", objectFit: "contain" }}
            />
          </div>

          {paymentAmount ? (
            <div>
              <div style={{ color: "#6F6B7D", fontSize: "14px", marginBottom: "4px" }}>
                จำนวนเงินที่ต้องชำระ
              </div>
              <div style={{ color: "#10B981", fontSize: "24px", fontWeight: 700 }}>
                {paymentAmount} บาท
              </div>
            </div>
          ) : null}

          {saveMethod === "external" || saveMethod === "manual" ? (
            <ExternalBrowserNotice
              method={saveMethod}
              hint="หรือกดค้างที่รูป QR Code แล้วเลือกบันทึกรูปภาพ"
            />
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: "100%",
                  maxWidth: "320px",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 20px",
                  background: "#4263B5",
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: 500,
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                {isSaving
                  ? "กำลังบันทึก..."
                  : saveMethod === "share" ? "บันทึกรูป QR Code" : "ดาวน์โหลด QR Code"}
              </button>

              <div style={{ color: "#6F6B7D", fontSize: "13px", maxWidth: "320px" }}>
                {notice || (saveMethod === "share"
                  ? "แตะปุ่มแล้วเลือก \"บันทึกภาพ\" (Save Image) หรือกดค้างที่รูป QR Code แล้วเลือกบันทึกรูปภาพ"
                  : "หากไฟล์ไม่ถูกดาวน์โหลด ให้กดปุ่มอีกครั้ง หรือกดค้างที่รูป QR Code แล้วเลือกบันทึกรูปภาพ")}
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};

export default QrCodeViewerPage;
